import { useState, useMemo } from "react"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useTableSort } from "@/app/hooks/useTableSort"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { FilterBar } from "@/app/components/FilterBar/FilterBar"
import { SearchInput } from "@/app/components/FilterBar/SearchInput"
import { SelectFilter } from "@/app/components/FilterBar/SelectFilter"
import { SortIcon } from "@/app/components/SortIcon"
import { TableColumnHeader } from "@/app/components/TableColumnHeader"
import { TableEmptyState } from "@/app/components/TableEmptyState"
import { DataCard } from "@/app/components/DataCard"
import { MobileSortSelect } from "@/app/components/MobileSortSelect"
import { ORRIRow, type OrriEntry } from "./ORRIRow"

interface Props {
  data: OrriEntry[]
}

export function ORRITable({ data }: Props) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()

  const [strSearch, setStrSearch] = useState("")
  const [trFilter, setTrFilter] = useState("")
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)

  const { sortKey, sortDir, handleSort, sortRows } = useTableSort({
    rows: data,
    numericKeys: ["orri", "net_acres_burdened"],
  })

  const trOptions = useMemo(
    () => Array.from(new Set(data.map((r) => r.township_range).filter(Boolean))).sort(),
    [data]
  )

  const filtered = useMemo(
    () =>
      data.filter((r) => {
        const strMatch =
          !strSearch ||
          r.str.toLowerCase().includes(strSearch.toLowerCase()) ||
          r.mineral_transaction.tx_number.toLowerCase().includes(strSearch.toLowerCase())
        const trMatch = !trFilter || r.township_range === trFilter
        return strMatch && trMatch
      }),
    [data, strSearch, trFilter]
  )

  const sorted = useMemo(() => sortRows(filtered), [filtered, sortRows])

  const hasFilters = !!(strSearch || trFilter)
  const clearAll = () => {
    setStrSearch("")
    setTrFilter("")
  }

  const filteredBurdened = sorted.reduce((s, r) => s + Number(r.net_acres_burdened), 0)

  const orriValues = data.map((r) => Number(r.orri)).filter((v) => !isNaN(v))
  const orriMin = Math.min(...orriValues)
  const orriMax = Math.max(...orriValues)

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        borderColor: theme.borderColor,
        backgroundColor: theme.bgSurface,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <FilterBar
        hasFilters={hasFilters}
        onClear={clearAll}
        resultCount={sorted.length}
        totalCount={data.length}
        entityLabel="entry"
        theme={theme}
      >
        <SearchInput
          value={strSearch}
          onChange={setStrSearch}
          placeholder="Search STR or TX #…"
          theme={theme}
        />
        <SelectFilter
          value={trFilter}
          onChange={setTrFilter}
          placeholder="All T-R"
          options={trOptions}
          theme={theme}
        />
      </FilterBar>

      {/* Mobile: card reflow. Desktop: full table. */}
      {isMobile ? (
        sorted.length === 0 ? (
          <div
            className="px-5 py-10 text-center"
            style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textMuted }}
          >
            No entries match your filters
          </div>
        ) : (
          <>
            <MobileSortSelect
              options={[
                { key: "str", label: "STR" },
                { key: "orri", label: "ORRI %" },
                { key: "net_acres_burdened", label: "Acres Burdened" },
              ]}
              sortKey={sortKey as string | null}
              sortDir={sortDir}
              onSort={(k) => handleSort(k as keyof OrriEntry)}
            />
            <div className="flex flex-col gap-2.5 p-3">
              {sorted.map((entry) => {
                const orriNum = Number(entry.orri)
                return (
                  <DataCard
                    key={entry.id}
                    title={entry.mineral_transaction.tx_number}
                    accessory={
                      <span
                        className="inline-block px-2.5 py-0.5 rounded-full"
                        style={{
                          fontFamily: "var(--font-data)",
                          fontSize: "11px",
                          fontWeight: 600,
                          backgroundColor: `${theme.brandTeal}20`,
                          color: theme.brandTeal,
                        }}
                      >
                        {(orriNum * 100).toFixed(4)}%
                      </span>
                    }
                    fields={[
                      { label: "Date", value: entry.mineral_transaction.transaction_date },
                      { label: "STR", value: entry.str, mono: true },
                      { label: "T-R", value: entry.township_range },
                      {
                        label: "Acres Burdened",
                        value: Number(entry.net_acres_burdened).toFixed(4),
                        mono: true,
                      },
                    ]}
                  />
                )
              })}
            </div>
          </>
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr
                style={{
                  backgroundColor: theme.bgSurface2,
                  borderBottom: `1px solid ${theme.borderColor}`,
                }}
              >
                <th className="px-5 py-3 text-left">
                  <TableColumnHeader label="TX #" theme={theme} />
                </th>
                <th className="px-5 py-3 text-left">
                  <TableColumnHeader label="Date" theme={theme} />
                </th>
                <th className="px-5 py-3 text-left">
                  <button className="flex items-center gap-1" onClick={() => handleSort("str")}>
                    <TableColumnHeader label="STR" theme={theme} />
                    <SortIcon col="str" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                  </button>
                </th>
                <th className="px-5 py-3 text-left">
                  <TableColumnHeader label="T-R" theme={theme} />
                </th>
                <th className="px-5 py-3 text-left">
                  <button className="flex items-center gap-1" onClick={() => handleSort("orri")}>
                    <TableColumnHeader label="ORRI %" theme={theme} />
                    <SortIcon col="orri" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                  </button>
                </th>
                <th className="px-5 py-3 text-right">
                  <button
                    className="flex items-center gap-1 ml-auto"
                    onClick={() => handleSort("net_acres_burdened")}
                  >
                    <TableColumnHeader label="Acres Burdened" theme={theme} />
                    <SortIcon
                      col="net_acres_burdened"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      theme={theme}
                    />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <TableEmptyState
                  message="No entries match your filters"
                  onClear={clearAll}
                  colSpan={6}
                  theme={theme}
                />
              ) : (
                sorted.map((entry, index) => (
                  <ORRIRow
                    key={entry.id}
                    entry={entry}
                    index={index}
                    hoveredRow={hoveredRow}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    orriMin={orriMin}
                    orriMax={orriMax}
                    theme={theme}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div
        className="flex items-center justify-between gap-2 flex-wrap px-5 py-3 border-t"
        style={{ borderTopColor: theme.borderColor, backgroundColor: theme.bgSurface2 }}
      >
        <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}>
          {hasFilters
            ? `Showing ${sorted.length} of ${data.length} entries`
            : `${data.length} total entries`}
        </span>
        <span
          style={{
            fontFamily: "var(--font-data)",
            fontSize: "12px",
            fontWeight: 500,
            color: theme.textMuted,
          }}
        >
          Total burdened:{" "}
          <span style={{ color: theme.textPrimary }}>{filteredBurdened.toFixed(4)} net acres</span>
        </span>
      </div>
    </div>
  )
}
